/**
 * TouchControlsShell — flag-gated touch control prototype surface.
 *
 * Mounted once from `GameUIOverlay.tsx`. Default OFF: with no
 * `?touchControls=1` in the URL the component renders nothing at all (no DOM,
 * no listeners, zero behaviour change on desktop).
 *
 * When enabled it renders the left-thumb movement pad and the right-thumb
 * ability/attack pad plus a one-line status readout. Intents go only to the
 * adapter from `getTouchControlAdapter()` — the shipped default is the logging
 * stub, so the prototype never moves, casts, or emits keyboard events.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { AbilityPad } from './AbilityPad';
import { EngineTouchControlAdapter } from './EngineTouchControlAdapter';
import { JoystickPad } from './JoystickPad';
import { getTouchControlAdapter, setTouchControlAdapter } from './touchControlAdapter';
import { isTouchControlsEnabled } from './touchControlsFlag';
import type { TouchControlAdapter } from './types';
import { useFullscreen } from '../hooks/useFullscreen';

export interface TouchControlsShellProps {
  adapter?: TouchControlAdapter;
  currentPage?: number;
  totalPages?: number;
  onPageFlip?: () => void;
}

export const TouchControlsShell: React.FC<TouchControlsShellProps> = ({
  adapter: propAdapter,
  currentPage,
  totalPages,
  onPageFlip,
}) => {
  // Read once per mount: the URL flag is an entry-time choice, not a live
  // setting. `isTouchControlsEnabled()` returns false outside the browser.
  const [enabled] = useState<boolean>(() => isTouchControlsEnabled());
  const [status, setStatus] = useState<string>('ready');
  const onTelemetry = useCallback((label: string) => setStatus(label), []);
  const { isFullscreen, toggle: toggleFs } = useFullscreen();
  const [fullscreenNotice, setFullscreenNotice] = useState<string | null>(null);

  const handleToggleFullscreen = useCallback(async () => {
    const res = await toggleFs();
    if (res.iosTip) {
      const isChrome = typeof navigator !== 'undefined' && /CriOS/i.test(navigator.userAgent);
      const tip = isChrome
        ? 'Chrome on iOS: Tap Share ⎋ → "Add to Home Screen" for true full screen'
        : 'iOS: Tap Share ⎋ → "Add to Home Screen" for true full screen';
      setFullscreenNotice(tip);
      setTimeout(() => setFullscreenNotice(null), 5000);
    }
  }, [toggleFs]);

  const [activeAdapter, setActiveAdapter] = useState<TouchControlAdapter>(() => {
    if (propAdapter) return propAdapter;
    const current = getTouchControlAdapter();
    if (enabled && current.id === 'stub') {
      const engine = new EngineTouchControlAdapter();
      setTouchControlAdapter(engine);
      return engine;
    }
    return current;
  });

  useEffect(() => {
    if (!enabled) return undefined;

    if (propAdapter) {
      setActiveAdapter(propAdapter);
      return undefined;
    }

    const currentAdapter = getTouchControlAdapter();
    if (currentAdapter instanceof EngineTouchControlAdapter) {
      const wireInput = () => {
        const inputSystem = ServiceRegistry.getInstance().get('InputSystem', { optional: true });
        if (inputSystem) {
          inputSystem.setVirtualInputSource(currentAdapter);
        }
      };

      wireInput();
      const unsub = EventBus.getInstance().on(EVENTS.GAME_SCENE_READY, wireInput);

      return () => {
        unsub();
        const inputSystem = ServiceRegistry.getInstance().get('InputSystem', { optional: true });
        if (inputSystem) {
          inputSystem.setVirtualInputSource(null);
        }
        setTouchControlAdapter(null);
      };
    }
    return undefined;
  }, [enabled, propAdapter]);

  if (!enabled) return null;

  const adapter = propAdapter ?? activeAdapter;

  return (
    <div
      data-testid="touch-controls-shell"
      data-touch-adapter={adapter.id}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        pointerEvents: 'none',
      }}
    >
      <div
        data-testid="touch-controls-status"
        style={{
          position: 'fixed',
          top: 74,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: '86vw',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '4px 12px',
          borderRadius: 12,
          background: 'rgba(10, 16, 24, 0.82)',
          border: '1px solid rgba(126, 200, 255, 0.5)',
          color: 'rgba(220, 240, 255, 0.95)',
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.02em',
          pointerEvents: 'none',
        }}
      >
        {`Touch prototype · ${adapter.id} · ${status}`}
      </div>

      {/* Fullscreen Quick Toggle for Mobile — positioned clear of the top-right minimap */}
      <button
        type="button"
        data-testid="touch-fullscreen-btn"
        aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        onClick={handleToggleFullscreen}
        style={{
          position: 'fixed',
          top: 'max(10px, env(safe-area-inset-top, 10px))',
          right: 'max(210px, calc(env(safe-area-inset-right, 0px) + 210px))',
          zIndex: 500,
          pointerEvents: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderRadius: 10,
          background: 'rgba(10, 16, 24, 0.88)',
          border: '1px solid rgba(126, 200, 255, 0.65)',
          color: 'rgba(220, 240, 255, 0.95)',
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
        }}
      >
        <span style={{ fontSize: 13 }}>{isFullscreen ? '⤦' : '⛶'}</span>
        <span>{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
      </button>

      {fullscreenNotice && (
        <div
          data-testid="touch-fullscreen-notice"
          role="status"
          style={{
            position: 'fixed',
            top: 'max(46px, calc(env(safe-area-inset-top, 10px) + 36px))',
            right: 'max(210px, calc(env(safe-area-inset-right, 0px) + 210px))',
            maxWidth: 280,
            padding: '6px 10px',
            borderRadius: 8,
            background: 'rgba(20, 30, 44, 0.95)',
            border: '1px solid rgba(255, 200, 80, 0.7)',
            color: '#ffebaa',
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            lineHeight: 1.3,
            zIndex: 501,
            pointerEvents: 'none',
          }}
        >
          {fullscreenNotice}
        </div>
      )}

      <JoystickPad
        adapter={adapter}
        onTelemetry={onTelemetry}
        style={{
          position: 'fixed',
          left: 'max(16px, env(safe-area-inset-left, 16px))',
          bottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
        }}
      />

      <AbilityPad
        adapter={adapter}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageFlip={onPageFlip}
        onTelemetry={onTelemetry}
        style={{
          position: 'fixed',
          right: 'max(8px, env(safe-area-inset-right, 8px))',
          bottom: 'max(8px, env(safe-area-inset-bottom, 8px))',
        }}
      />
    </div>
  );
};
